import parse from "html-react-parser"
import { useTranslation } from "react-i18next"

import { CourseList } from "@/features/training/components/course-list"
import {
  DemoActiveCitations,
  DemoActivityMessages,
  DemoCompanyLibraryModal,
  DemoFactCheckPanel,
  DemoResearchPlan,
  DemoResearchSettings,
  DemoTrustedSourcesMenu,
} from "@/features/training/components/demo"
import { CourseLayout } from "@/features/training/components/layouts/course-layout"
import { CourseSection } from "@/features/training/components/layouts/course-section"
import { ensureArray } from "@/features/training/utils/ensure-array"

export function AIExplorerSearchAndVerifyPage() {
  const { t } = useTranslation("", { keyPrefix: "training.ai-explorer.search-and-verify" })
  const { t: tRA } = useTranslation("", { keyPrefix: "training.ai-explorer.research-agent" })
  const list = <T = string,>(key: string) => ensureArray<T>(t(key, { returnObjects: true }))
  const listRA = <T = string,>(key: string) => ensureArray<T>(tRA(key, { returnObjects: true }))

  return (
    <CourseLayout>
      <CourseSection>
        <h1>{t("title")}</h1>
        <h2>{t("purpose.title")}</h2>
        <p>{parse(t("purpose.description"))}</p>
        <p>
          <strong>{t("purpose.level")}</strong>
        </p>
      </CourseSection>

      <CourseSection>
        <h2>{t("learning_objectives.title")}</h2>
        <p>{t("learning_objectives.description")}</p>
        <CourseList data={list("learning_objectives.objectives")} />
      </CourseSection>

      <CourseSection>
        <h2>{t("overview.title")}</h2>
        <p>{parse(t("overview.intro"))}</p>
        <CourseList data={list("overview.sources")} />
        <p>{t("overview.additional")}</p>
        <CourseList data={list("overview.tools")} />
        <p>{parse(t("overview.goal"))}</p>
      </CourseSection>

      <CourseSection>
        <h2>{t("web_search.title")}</h2>
        <h3>{t("web_search.how_it_works.title")}</h3>
        <CourseList data={list("web_search.how_it_works.description")} />
        <p>{parse(t("web_search.when_to_use"))}</p>

        <h3>{t("web_search.use_cases.title")}</h3>
        {list<{ title: string; points: string[] }>("web_search.use_cases.cases").map(
          (useCase, index) => (
            <div key={index}>
              <h4>{useCase.title}</h4>
              <CourseList data={ensureArray<string>(useCase.points)} />
            </div>
          )
        )}
      </CourseSection>

      <CourseSection>
        <h2>{t("m365_search.title")}</h2>
        <h3>{t("m365_search.how_it_works.title")}</h3>
        <p>{t("m365_search.how_it_works.intro")}</p>
        <CourseList data={list("m365_search.how_it_works.sources")} />
        <p>{parse(t("m365_search.how_it_works.usage"))}</p>

        <h3>{t("m365_search.use_cases.title")}</h3>
        <CourseList data={list("m365_search.use_cases.examples")} />
      </CourseSection>

      <CourseSection>
        <h2>{t("company_libraries.title")}</h2>
        <h3>{t("company_libraries.how_it_works.title")}</h3>
        <p>{parse(t("company_libraries.how_it_works.description"))}</p>
        <div className="my-4">
          <DemoCompanyLibraryModal />
        </div>

        <h3>{t("company_libraries.manage.title")}</h3>
        <CourseList data={list("company_libraries.manage.steps")} />
        <p>
          <strong>{t("company_libraries.manage.tips_label")}</strong>
        </p>
        <CourseList data={list("company_libraries.manage.tips")} />

        <h3>{t("company_libraries.use_cases.title")}</h3>
        <CourseList data={list("company_libraries.use_cases.examples")} />
      </CourseSection>

      <CourseSection>
        <h2>{t("mini_exercise.title")}</h2>
        <p>{parse(t("mini_exercise.intro"))}</p>
        <CourseList data={list("mini_exercise.steps")} />
        <p>{parse(t("mini_exercise.reflection"))}</p>
      </CourseSection>

      <CourseSection>
        <h2>{t("trusted_sources.title")}</h2>
        <h3>{t("trusted_sources.how_it_works.title")}</h3>
        <p>{parse(t("trusted_sources.how_it_works.description"))}</p>
        <div className="my-4 flex items-center gap-3">
          <DemoTrustedSourcesMenu />
          <span className="text-muted-foreground text-sm">{t("sources.trusted_sources")}</span>
        </div>

        <h3>{t("trusted_sources.when_important.title")}</h3>
        <CourseList data={list("trusted_sources.when_important.situations")} />

        <h3>{t("trusted_sources.use_case.title")}</h3>
        <CourseList data={list("trusted_sources.use_case.points")} />
      </CourseSection>

      <CourseSection>
        <h2>{t("fact_check.title")}</h2>
        <h3>{t("fact_check.how_it_works.title")}</h3>
        <CourseList data={list("fact_check.how_it_works.steps")} />
        <p>{parse(t("fact_check.how_it_works.result"))}</p>
        <div className="my-4">
          <DemoFactCheckPanel />
        </div>

        <h3>{t("fact_check.use_cases.title")}</h3>
        {list<{ title: string; points: string[] }>("fact_check.use_cases.cases").map(
          (useCase, index) => (
            <div key={index}>
              <h4>{useCase.title}</h4>
              <CourseList data={ensureArray<string>(useCase.points)} />
            </div>
          )
        )}
      </CourseSection>

      <CourseSection>
        <h2>{t("fact_check_exercise.title")}</h2>
        <p>{parse(t("fact_check_exercise.intro"))}</p>
        <CourseList data={list("fact_check_exercise.steps")} />
        <p>{parse(t("fact_check_exercise.reflection"))}</p>
      </CourseSection>

      <CourseSection>
        <h2>{t("activity_messages.title")}</h2>
        <p>{parse(t("activity_messages.description"))}</p>
        <div className="my-4">
          <DemoActivityMessages />
        </div>
        <p>
          <strong>{t("activity_messages.value_label")}</strong>
        </p>
        <CourseList data={list("activity_messages.value")} />
      </CourseSection>

      <CourseSection>
        <h2>{t("active_citations.title")}</h2>
        <p>{parse(t("active_citations.description"))}</p>
        <div className="my-4">
          <DemoActiveCitations />
        </div>
        <CourseList data={list("active_citations.steps")} />
        <p>{parse(t("active_citations.combine"))}</p>
      </CourseSection>

      {/* Research Agent — advanced section */}
      <CourseSection>
        <h2>{tRA("why_use.title")}</h2>
        <p>{parse(tRA("why_use.description"))}</p>
        <CourseList data={listRA("why_use.when_to_use")} />
        <p>{parse(tRA("why_use.note"))}</p>
      </CourseSection>

      <CourseSection>
        <h2>{tRA("settings.title")}</h2>
        <p>{parse(tRA("settings.intro"))}</p>
        <div className="my-4">
          <DemoResearchSettings />
        </div>
        {listRA<{ title: string; description?: string; values: string[]; note?: string }>(
          "settings.options"
        ).map((option, index) => (
          <div key={index}>
            <h3>{option.title}</h3>
            {option.description && (
              <p className="text-muted-foreground">{parse(option.description)}</p>
            )}
            <CourseList data={ensureArray<string>(option.values)} />
            {option.note && <p>{parse(option.note)}</p>}
          </div>
        ))}
      </CourseSection>

      <CourseSection>
        <h2>{tRA("research_plan.title")}</h2>
        <p>{parse(tRA("research_plan.description"))}</p>
        <div className="my-4">
          <DemoResearchPlan />
        </div>
      </CourseSection>

      <CourseSection>
        <h2>{t("summary.title")}</h2>
        <CourseList data={list("summary.points")} />
        <p>{parse(t("summary.next_module"))}</p>
      </CourseSection>
    </CourseLayout>
  )
}
